import {DateTime} from '../services/dateTime';
import {highestPatchAwards,romanLevel} from '../services/patchProgression';
import {patchEarnedDate} from '../services/patchDates';
import React, { useEffect, useRef, useState } from "react";
import { badgeRequest,loadOwnBadgeState, BadgeState } from "../services/badgeApi";
import BadgeArtwork from "./BadgeArtwork";
import {useGuest} from '../services/GuestContext';
import BadgeRarity from './BadgeRarity';
import {useBadgeScope} from '../services/useBadgeScope';
export default function ProfileAwards({
  farmId,
  email,
  admin = false,
}: {
  farmId: string;
  email?: string;
  admin?: boolean;
}) {
  const {farm}=useGuest();
  const scope=useBadgeScope()+JSON.stringify([farmId,email,admin]),currentScope=useRef(scope);currentScope.current=scope;
  const [result,setResult]=useState<{scope:string;data:BadgeState}|null>(null);
  const data=result?.scope===scope?result.data:null;
  const setData=(data:BadgeState|null)=>setResult(data?{scope,data}:null);
  const [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [revision, setRevision] = useState(0);
  const [adjustment, setAdjustment] = useState<{
      id: string;
      revoke: boolean;
    } | null>(null),
    [reason, setReason] = useState("");
  useEffect(() => {
    let current = true;
    setData(null);
    setError("");
    setBusy(false);setAdjustment(null);setReason('');
    if (farmId)
      void (admin||email ? badgeRequest<BadgeState>(
        farmId,
        "",
        "GET",
        undefined,
        email ? { email } : {},
      ) : loadOwnBadgeState(farmId))
        .then((result) => {
          if (current&&currentScope.current===scope) setData(result);
        })
        .catch((e) => {
          if (current&&currentScope.current===scope) setError(e.message);
        });
    return () => {
      current = false;
    };
  }, [scope, revision]);
  const adjust = async () => {
    if (!adjustment) return;
    setBusy(true);
    try {
      await badgeRequest(farmId, "/awards/" + adjustment.id, "PUT", {
        action: adjustment.revoke ? "revoke" : "reinstate",
        reason,
      });
      if(currentScope.current!==scope)return;
      setAdjustment(null);
      setReason("");
      setRevision((x) => x + 1);
    } catch (e) {
      if(currentScope.current===scope)setError((e as Error).message);
    } finally {
      if(currentScope.current===scope)setBusy(false);
    }
  };
  const visibility = async () => {
    if (!data) return;
    setBusy(true);
    try {
      await badgeRequest(farmId, "/visibility", "PUT", {
        visible: !data.leaderboard_visible,
      });
      if(currentScope.current!==scope)return;
      setData({ ...data, leaderboard_visible: !data.leaderboard_visible });
    } catch (e) {
      if(currentScope.current===scope)setError((e as Error).message);
    } finally {
      if(currentScope.current===scope)setBusy(false);
    }
  };
  return (
    <section className="bg-white border rounded-2xl p-5 sm:p-6 space-y-4">
      <div className="flex justify-between gap-3">
        <h2 className="font-semibold">{admin ? "Awards" : "Patches"}</h2>
        <span className="text-xs text-gray-500">
          {admin?`${data?data.points:'—'} points`:`${data?highestPatchAwards(data.awards).length:'—'} earned`}
        </span>
      </div>
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      {data?.offlineSavedAt&&<p role="status" className="text-sm text-gray-500">Showing saved patches · last refreshed {<DateTime value={data.offlineSavedAt}/>}</p>}
      {!data && !error && farmId && <p role="status">Loading recognition…</p>}
      {data && !data.awards.length && (
        <p className="text-sm text-gray-500">
          {admin
            ? "No badges earned yet."
            : "Your recorded recognition will appear here."}
        </p>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        {(data ? admin ? data.awards : highestPatchAwards(data.awards) : [])
          .filter((a) => admin || !a.revoked_at)
          .map((award) => (
            <article
              key={award.award_id}
              className={`border rounded-xl p-4 ${award.revoked_at ? "opacity-60" : ""}`}
            >
              <div className="flex gap-3 items-center">
                <BadgeArtwork image={admin?award.image:award.display_image||award.image} name={admin?award.name:award.display_name||award.name} property={farm?.farm_id===farmId?farm.name:undefined} medal={award.presentation_kind==='medal'}/>
                <div>
                  <h3 className="font-semibold">{admin?award.name:award.display_name||award.name}</h3>
                  <p className="text-sm text-gray-600">
                    {admin&&`${award.points} points · `}
                    {patchEarnedDate(admin?null:award.earned_on,award.awarded_at).toLocaleDateString()}
                  </p>
                  {award.revoked_at && <p className="text-sm">Revoked</p>}
                </div>
              </div>
              <p className="mt-3 text-sm text-gray-600">{award.description}</p>
              {award.story&&<p className="mt-2 text-sm text-gray-600 whitespace-pre-wrap">{award.story}</p>}
              {!admin&&<BadgeRarity value={data?.statistics?.find(s=>s.badge_id===award.badge_id)}/>}
              {admin && (
                <>
                  <p className="text-xs text-gray-500 mt-3">
                    {award.progression_level?`Level ${romanLevel(award.progression_level)} · `:''}Rule version {award.rule_version} ·{" "}
                    {award.source.type === "visit_count"
                      ? `${award.source.count} stays: ${award.source.visit_ids.join(", ")}`
                      : award.source.type === "poi_arrival"
                        ? `Place: ${award.source.poi_id}`
                        : award.source.type==='onboarding'?'Welcome to the property'
                        : award.source.type==='manual'?`Assigned by ${award.source.actor_email}${award.source.reason?' · '+award.source.reason:''}`
                        : `${award.source.count} qualifying records${!('seconds' in award.source) || award.source.seconds === undefined ? "" : ` · ${award.source.seconds} active seconds`}`}{" "}
                  </p>
                  {(award.source.type==='project_count'||award.source.type==='work_minutes'||award.source.type==='photo_count') && (
                    <ul className="text-xs mt-2">
                      {award.source.sources.map((s) => (
                        <li key={s.id || s.photo_id}>
                          <a
                            className="underline"
                            href={
                              s.photo_id
                                ? `/gallery/${encodeURIComponent(s.photo_id)}`
                                : `/settings/moderation/contributions#work-${s.id}`
                            }
                          >
                            {s.photo_id ? "Photo" : "Work"} {s.photo_id || s.id}{" "}
                            · revision {s.revision}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                  <button
                    className="underline text-sm mt-3"
                    onClick={() => {
                      setAdjustment({
                        id: award.award_id,
                        revoke: !award.revoked_at,
                      });
                      setReason("");
                    }}
                  >
                    {award.revoked_at ? "Reinstate" : "Revoke"} award
                  </button>
                </>
              )}
            </article>
          ))}
      </div>
      {data&&adjustment && (
        <div className="border rounded-xl p-4 space-y-3">
          <label className="block text-sm">
            Reason for {adjustment.revoke ? "revoking" : "reinstating"} this
            award
            <textarea
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
              className="block w-full border rounded-lg p-2 mt-2"
            />
          </label>
          <button
            disabled={busy || !reason.trim()}
            className="bg-usda-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
            onClick={() => void adjust()}
          >
            Save adjustment
          </button>
          <button
            className="ml-4 underline"
            onClick={() => setAdjustment(null)}
          >
            Cancel
          </button>
        </div>
      )}
      {!admin && data && (
        <label className="flex gap-3 items-center text-sm border-t pt-4">
          <input
            type="checkbox"
            checked={data.leaderboard_visible}
            disabled={busy}
            onChange={() => void visibility()}
          />
          Share my earned collection
        </label>
      )}
      {admin && !!data?.adjustments.length && (
        <details>
          <summary className="cursor-pointer text-sm font-semibold">
            Award adjustments
          </summary>
          <ol className="mt-3 space-y-3">
            {data.adjustments.map((a) => (
              <li key={a.adjustment_id} className="text-sm">
                <p>
                  {a.action} · {a.reason}
                </p>
                <p className="text-xs text-gray-500">
                  {a.actor_email} · {<DateTime value={a.created_at}/>}
                </p>
              </li>
            ))}
          </ol>
        </details>
      )}
    </section>
  );
}
